import { useState, useCallback, useEffect } from 'react';
import { Pagination, SelectItemProps } from '@ui/src';
import { SicCode } from '../types';
import { useLazyGetCustomersSicCodesQuery } from '../services';

interface ListResponse {
  data: { data: { edges: { node: SicCode }[]; pageInfo: Pagination } };
}

let timeout: NodeJS.Timeout;

const useGetAutocompleteList = () => {
  const [autocompleteList, setAutocompleteList] = useState<SelectItemProps[]>(
    [],
  );
  const [pagination, setPagination] = useState<Pagination | undefined>(
    undefined,
  );
  const [searchQuery, setSearchQuery] = useState('');

  const [getList, { isLoading, isFetching }] =
    useLazyGetCustomersSicCodesQuery();

  const getAutocompleteListAction = useCallback(async (query?: string) => {
    const results: SelectItemProps[] = [];

    if (/[0-9]/.test(query as string)) {
      const codeResponse = (await getList(
        `?take=40${query ? `&code=${query}` : ''}`,
      )) as ListResponse;

      if (codeResponse?.data?.data) {
        codeResponse.data.data.edges?.forEach((item) => {
          results.push({
            name: item.node.code,
            value: item.node.code,
            description: item.node.description,
            suffix: `Risk score: ${item.node.riskScore}`,
          });
        });

        if (codeResponse?.data?.data?.edges.length) {
          setPagination(codeResponse?.data?.data?.pageInfo);

          if (query) {
            setSearchQuery(`&code=${query}`);
          }
        }
      }
    } else if (query && !/[0-9]/.test(query)) {
      const descriptionResponse = (await getList(
        `?take=40${query ? `&description=${query}` : ''}`,
      )) as ListResponse;

      if (descriptionResponse?.data?.data) {
        descriptionResponse.data.data.edges?.forEach((item) => {
          results.push({
            name: item.node.code,
            value: item.node.code,
            description: item.node.description,
            suffix: `Risk score: ${item.node.riskScore}`,
          });
        });

        if (descriptionResponse?.data?.data?.edges.length) {
          setPagination(descriptionResponse?.data?.data?.pageInfo);

          if (query) {
            setSearchQuery(`&description=${query}`);
          }
        }
      }
    } else {
      const response = (await getList('?take=40')) as ListResponse;

      if (response?.data?.data) {
        response.data.data.edges?.forEach((item) => {
          results.push({
            name: item.node.code,
            value: item.node.code,
            description: item.node.description,
            suffix: `Risk score: ${item.node.riskScore}`,
          });
        });

        if (response?.data?.data?.edges.length) {
          setPagination(response?.data?.data?.pageInfo);
          setSearchQuery('');
        }
      }
    }

    setAutocompleteList(results);
  }, []);

  const onSearch = (query: string, withoutTimeout?: boolean) => {
    if (withoutTimeout) {
      getAutocompleteListAction(query);
    } else {
      clearTimeout(timeout);

      timeout = setTimeout(() => {
        getAutocompleteListAction(query);
      }, 300);
    }
  };

  useEffect(() => {
    getAutocompleteListAction();
  }, [getAutocompleteListAction]);

  return {
    getAutocompleteListAction,
    autocompleteList,
    isLoading: isLoading || isFetching,
    onSearch,
    pagination,
    searchQuery,
    setAutocompleteList,
    setPagination,
  };
};

export default useGetAutocompleteList;
